import React, { useState, useEffect } from "react";
import { Tournament, Match } from "../types/tournament";
import { Trophy, Shield, Target, TrendingUp, Users } from "lucide-react";

interface LiveDashboardProps {
  tournament: Tournament;
  showLiveScores?: boolean;
  liveScores?: { [key: string]: { team1: number; team2: number } };
}

const LiveDashboard: React.FC<LiveDashboardProps> = ({
  tournament,
  showLiveScores = true,
  liveScores = {},
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const currentRound = tournament.rounds[tournament.currentRound - 1];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getCourt1Defender = () => {
    return tournament.teams.find((team) => team.currentCourt === 1);
  };

  const court1Defender = getCourt1Defender();

  return (
    <div className="w-full bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-6xl font-bold mb-4">🏓 {tournament.name}</h1>
        <div className="text-2xl text-blue-200">
          Round {tournament.currentRound} of {tournament.totalRounds}
        </div>
        <div className="text-xl text-blue-300 mt-2">
          {formatTime(currentTime)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Court 1 Defender */}
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 shadow-lg">
          <div className="text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold mb-2">Court 1 Defender</h2>
            {court1Defender ? (
              <div>
                <div className="text-4xl font-bold mb-2">
                  {court1Defender.name}
                </div>
                <div className="text-xl mb-2">
                  {court1Defender.player1} & {court1Defender.player2}
                </div>
                <div
                  className={`text-2xl font-bold ${
                    court1Defender.court1Streak >= 3 ? "animate-bounce" : ""
                  }`}
                >
                  {court1Defender.court1Streak} Round
                  {court1Defender.court1Streak !== 1 ? "s" : ""} Defending
                </div>
                {court1Defender.court1Streak >= 3 && (
                  <div className="mt-4 text-lg font-semibold animate-pulse">
                    🛡️ Iron Wall Achievement!
                  </div>
                )}
              </div>
            ) : (
              <div className="text-2xl">No current defender</div>
            )}
          </div>
        </div>

        {/* Match of the Round */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 shadow-lg">
          <div className="text-center">
            <Target className="w-16 h-16 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold mb-4">Match of the Round</h2>
            {currentRound?.matchOfTheRound ? (
              <div>
                <div className="text-2xl font-bold mb-2">
                  Court {currentRound.matchOfTheRound.court}
                </div>
                <div className="text-xl mb-2">
                  {currentRound.matchOfTheRound.team1.name} vs{" "}
                  {currentRound.matchOfTheRound.team2.name}
                </div>
                {currentRound.matchOfTheRound.result && (
                  <div className="text-3xl font-bold">
                    {currentRound.matchOfTheRound.result.team1Score} -{" "}
                    {currentRound.matchOfTheRound.result.team2Score}
                  </div>
                )}
                <div className="text-lg mt-2">Closest Match!</div>
              </div>
            ) : (
              <div className="text-2xl">No match selected yet</div>
            )}
          </div>
        </div>

        {/* Tournament Stats */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 shadow-lg">
          <div className="text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold mb-4">Tournament Stats</h2>
            <div className="space-y-3 text-xl">
              <div>{tournament.teams.length} Teams</div>
              <div>{tournament.currentRound - 1} Rounds Completed</div>
              <div>
                {tournament.teams.reduce((total, team) => total + team.wins, 0)}{" "}
                Total Wins
              </div>
              <div>
                {tournament.teams.reduce(
                  (total, team) => total + team.perfectGames,
                  0
                )}{" "}
                Perfect Games
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Matches Grid */}
      {showLiveScores && (
        <div className="mt-12">
          <h2 className="text-4xl font-bold text-center mb-8">Live Matches</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: tournament.courts.length }, (_, index) => {
              const courtNumber = index + 1;
              const match = currentRound?.matches.find(
                (m) => m.court === courtNumber
              );

              return (
                <div
                  key={courtNumber}
                  className={`bg-white/10 backdrop-blur-sm rounded-lg p-6 border-2 transition-all duration-300 hover:scale-105 ${
                    match?.isMatchOfTheRound
                      ? "border-red-400 shadow-lg shadow-red-500/50 animate-pulse"
                      : match?.court === 1
                      ? "border-yellow-400 shadow-lg shadow-yellow-500/50 animate-pulse"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <div className="text-center">
                    <h3 className="text-2xl font-bold mb-4">
                      Court {courtNumber}
                    </h3>

                    {match ? (
                      <div className="space-y-4">
                        {match.isBye ? (
                          <div>
                            <div className="text-xl font-semibold">
                              {match.team1.name}
                            </div>
                            <div className="text-lg text-blue-200">
                              Bye Round
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-2">
                              <div className="text-lg font-semibold">
                                {match.team1.name}
                              </div>
                              <div className="text-sm text-blue-200">
                                {match.team1.player1} & {match.team1.player2}
                              </div>
                            </div>

                            <div className="text-2xl font-bold">VS</div>

                            <div className="space-y-2">
                              <div className="text-lg font-semibold">
                                {match.team2.name}
                              </div>
                              <div className="text-sm text-blue-200">
                                {match.team2.player1} & {match.team2.player2}
                              </div>
                            </div>

                            {/* Live Score Display */}
                            {liveScores[match.id] && !match.result && (
                              <div className="mt-4">
                                <div className="text-3xl font-bold text-blue-300">
                                  {liveScores[match.id].team1} -{" "}
                                  {liveScores[match.id].team2}
                                </div>
                                <div className="text-sm text-blue-200">
                                  Live Score
                                </div>
                              </div>
                            )}

                            {match.result && (
                              <div className="mt-4">
                                <div className="text-3xl font-bold">
                                  {match.result.team1Score} -{" "}
                                  {match.result.team2Score}
                                </div>
                                {match.result.isPerfectGame && (
                                  <div className="text-lg text-yellow-300 mt-2">
                                    🏆 Perfect Game!
                                  </div>
                                )}
                              </div>
                            )}

                            {match.isMatchOfTheRound && (
                              <div className="mt-2 text-red-300 font-semibold">
                                🎯 Match of the Round
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-lg text-blue-200">
                        No match scheduled
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live Ladder Positions */}
      <div className="mt-12">
        <h2 className="text-4xl font-bold text-center mb-8">
          Live Ladder Positions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tournament.teams
            .sort((a, b) => a.currentCourt - b.currentCourt)
            .map((team, index) => (
              <div
                key={team.id}
                className={`bg-white/10 backdrop-blur-sm rounded-lg p-4 border-2 transition-all duration-300 hover:scale-105 ${
                  team.currentCourt === 1
                    ? "border-yellow-400 shadow-lg shadow-yellow-500/50 animate-pulse"
                    : "border-white/20 hover:border-white/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold">{team.name}</div>
                    <div className="text-sm text-blue-200">
                      Court {team.currentCourt}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{index + 1}</div>
                    <div className="text-sm text-blue-200">
                      {team.wins}W - {team.losses}L
                    </div>
                  </div>
                </div>
                {team.currentCourt === 1 && (
                  <div className="mt-2 text-center text-yellow-300 font-semibold">
                    👑 Champion Position
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LiveDashboard;
